import React, { useState, useContext, useEffect } from "react";
import "./AllNews.css";
import NewsDetailPopup from "../NewsDetailPopup/NewsDetailPopup";
import editIcon from "../../../images/edit-button.svg";
import deleteIcon from "../../../images/trash.svg";
import { UserContext } from "../../../contexts/CurrentUserContext";
import CreateNewsPopup from "../CreateNewsPopup/CreateNewsPopup";
import {
  getAllNews,
  createNews,
  getAPIURL,
  deleteNews,
  updateNews,
  getImageUrl,
} from "../../../api/newsGallery";
import EditNewsPopup from "../EditNewsPopup/EditNewsPopup";

function AllNews() {
  const { userData } = useContext(UserContext);
  const hasAdminAccess = userData?.access_levels?.some(
    (level) => level.name === "АДМС"
  );

  const hasPublicAccess = userData?.access_levels?.some(
    (level) => level.name === "ПУБ"
  );

  const canCreateNews = hasAdminAccess || hasPublicAccess;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNews, setSelectedNews] = useState(null);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingNews, setEditingNews] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsData = await getAllNews();
        const sortedNews = newsData.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setNews(sortedNews);
      } catch (error) {
        console.error("Ошибка при загрузке новостей:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const filteredNews = news.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNewsClick = (news) => {
    setSelectedNews(news);
  };

  const handleCloseNewsDetail = () => {
    setSelectedNews(null);
  };

  const handleEdit = (e, news) => {
    e.stopPropagation();
    setEditingNews(news);
  };

  const handleDelete = async (e, newsItem) => {
    e.stopPropagation();
    if (window.confirm("Вы уверены, что хотите удалить эту новость?")) {
      try {
        await deleteNews(newsItem.id);
        const updatedNews = await getAllNews();
        setNews(updatedNews);
      } catch (error) {
        console.error("Ошибка при удалении новости:", error);
      }
    }
  };

  const handleCreateClick = () => {
    setIsCreatePopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsCreatePopupOpen(false);
  };

  const handleCreateNews = async (newsData) => {
    try {
      const formData = new FormData();
      formData.append("title", newsData.title);
      formData.append("content", newsData.content);

      if (newsData.images && newsData.images.length > 0) {
        newsData.images.forEach((image) => {
          formData.append("images", image);
        });
      }

      await createNews(formData);
      const updatedNews = await getAllNews();
      setNews(updatedNews);
      setIsCreatePopupOpen(false);
    } catch (error) {
      console.error("Ошибка при создании новости:", error);
    }
  };

  const handleUpdateNews = async (formData) => {
    try {
      if (!formData) {
        throw new Error("Отсутствуют данные для обновления");
      }

      const updateFormData = new FormData();

      // Добавляем основные поля
      updateFormData.append("title", formData.get("title"));
      updateFormData.append("content", formData.get("content"));

      // Добавляем новые изображения
      const images = formData.getAll("images");
      images.forEach((image) => {
        updateFormData.append("images", image);
      });

      // Добавляем URLs изображений для удаления
      const removeImages = formData.getAll("remove_images");
      if (removeImages.length > 0) {
        removeImages.forEach((imageUrl) => {
          // Извлекаем только имя файла из полного URL
          const fileName = imageUrl.split("/").pop();
          updateFormData.append("remove_images", fileName);
        });
      }

      console.log("Отправляемые данные для удаления:", removeImages);

      await updateNews(editingNews.id, updateFormData);
      const updatedNews = await getAllNews();
      setNews(updatedNews);
      setEditingNews(null);
    } catch (error) {
      console.error("Ошибка при обновлении новости:", error);
      throw error;
    }
  };

  const canEditNews = (newsItem) => {
    console.log("Checking edit permissions:", {
      newsItem,
      author_id: newsItem.author_id,
      currentUserId: userData.id,
      hasAdminAccess,
      hasPublicAccess,
    });

    if (hasAdminAccess) return true;
    if (hasPublicAccess && newsItem.author_id === userData.id) return true;
    return false;
  };

  return (
    <div className="all-news">
      <div className="all-news__header">
        <h1 className="all-news__title">Все новости компании</h1>
        <div className="all-news__controls">
          {canCreateNews && (
            <button
              className="all-news__create-button"
              onClick={handleCreateClick}
            >
              Создать
            </button>
          )}
          <div className="all-news__search">
            <input
              type="text"
              placeholder="Поиск новостей..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="all-news__search-input"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <p>Загрузка новостей...</p>
      ) : (
        <div className="all-news__grid">
          {filteredNews.map((item) => (
            <article
              key={item.id}
              className="news-item"
              onClick={() => handleNewsClick(item)}
            >
              {(hasAdminAccess || hasPublicAccess) && canEditNews(item) && (
                <div className="news-item__admin-buttons">
                  <button
                    className="news-item__admin-button"
                    onClick={(e) => handleEdit(e, item)}
                  >
                    <img src={editIcon} alt="Редактировать" />
                  </button>
                  <button
                    className="news-item__admin-button"
                    onClick={(e) => handleDelete(e, item)}
                  >
                    <img src={deleteIcon} alt="Удалить" />
                  </button>
                </div>
              )}
              <div className="news-item__image-container">
                <img
                  className="news-item__image"
                  src={item.image_urls[0]}
                  alt={item.title}
                />
              </div>
              <div className="news-item__content">
                <div className="news-item__date">
                  {formatDate(item.created_at)}
                </div>
                <h3 className="news-item__title">{item.title}</h3>
                <p className="news-item__text">{item.content}</p>
              </div>
            </article>
          ))}
        </div>
      )}

      <NewsDetailPopup
        isOpen={selectedNews !== null}
        onClose={handleCloseNewsDetail}
        news={selectedNews}
      />

      <CreateNewsPopup
        isOpen={isCreatePopupOpen}
        onClose={handleClosePopup}
        onSubmit={handleCreateNews}
      />

      <EditNewsPopup
        isOpen={editingNews !== null}
        onClose={() => setEditingNews(null)}
        news={editingNews}
        onSubmit={handleUpdateNews}
      />
    </div>
  );
}

export default AllNews;
